import { useRef, useState } from 'react';
import { ImageUp, Pencil } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { setAvatarCritter, uploadAvatarImage } from '../lib/api';
import { CRITTERS, CRITTER_KEYS } from '../lib/critters';
import UserAvatar, { CritterIcon } from '../components/UserAvatar';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Spinner from '../components/Spinner';
import { cn } from '../lib/utils';

// Pick a pixel critter, upload a picture, or go back to initials — for the
// given person. Saves immediately; the caller refreshes the member list.
function AvatarPicker({ user, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const save = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save the avatar.');
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) save(() => uploadAvatarImage(user.id, file));
  };

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogHeader title={`avatar — ${user.name}`} onClose={onClose} />
      <DialogBody className="space-y-4">
        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        ) : null}
        <div className="flex items-center gap-3">
          <UserAvatar user={user} className="h-12 w-12 rounded-md text-base" />
          <div className="text-sm">
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground">
              {user.avatar_critter ? CRITTERS[user.avatar_critter]?.label : user.avatar_url ? 'uploaded picture' : 'initials'}
            </p>
          </div>
        </div>

        <div>
          <p className="mb-2 font-mono text-xs text-muted-foreground">{'// pick a critter'}</p>
          <div className="grid grid-cols-6 gap-2">
            {CRITTER_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                disabled={busy}
                onClick={() => save(() => setAvatarCritter(user.id, k))}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-lg border p-2 text-[10px] transition hover:border-primary/50 hover:bg-accent',
                  user.avatar_critter === k ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground',
                )}
                title={CRITTERS[k].label}
              >
                <CritterIcon kind={k} className="h-8 w-8" />
                {CRITTERS[k].label.toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} className="gap-1.5">
            <ImageUp className="h-3.5 w-3.5" /> upload a picture
          </Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} />
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => save(() => setAvatarCritter(user.id, ''))}>
            use initials
          </Button>
          <span className="text-xs text-muted-foreground">PNG / JPEG / GIF / WebP, up to 2 MB.</span>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Close</Button>
      </DialogFooter>
    </Dialog>
  );
}

export default function TeamPage() {
  const { members, myId, loading, refresh } = useTickets();
  const [editing, setEditing] = useState(null);

  const team = members
    .filter((m) => !m.is_external)
    .sort((a, b) => (
      (a.id === myId ? -1 : 0) - (b.id === myId ? -1 : 0)
      || (b.is_assignable ? 1 : 0) - (a.is_assignable ? 1 : 0)
      || a.name.localeCompare(b.name)
    ));
  const current = editing ? members.find((m) => m.id === editing) : null;

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4">
        <h1 className="font-mono text-lg font-semibold">{'// team'}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everyone with a login. Pick an avatar for yourself — or for a teammate who hasn&apos;t yet. It shows on
          board cards, in the ticket modal and on the tickets list.
        </p>
      </div>

      {loading && members.length === 0 ? <Spinner /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {team.map((m) => (
            <div key={m.id} className={cn('flex items-center gap-3 rounded-xl border border-border bg-card p-3', m.id === myId && 'border-primary/40')}>
              <UserAvatar user={m} className="h-10 w-10 rounded-md text-sm" />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                  {m.name}
                  {m.id === myId ? <span className="rounded bg-primary/15 px-1 text-[10px] font-semibold uppercase text-primary">you</span> : null}
                </p>
                <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                  @{m.username}
                  {m.is_assignable ? <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">dev</Badge> : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditing(m.id)}
                className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground"
                title={`Change ${m.name}'s avatar`}
              >
                <Pencil className="h-3 w-3" /> avatar
              </button>
            </div>
          ))}
        </div>
      )}

      {current ? <AvatarPicker user={current} onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </div>
  );
}
